import { useEffect, useMemo, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Shield, Users2, FolderTree, KeyRound, UserCheck, Plus, Trash2, Save, X, ChevronRight, Search } from 'lucide-react';
import AuthGuard from '../components/auth/AuthGuard';
import AccessExplorer from '../components/access/AccessExplorer';
import { rbacApi, organizationApi, projectApi, userApi, apiTokenApi } from '../services/api';

type Tab = 'roles' | 'usergroups' | 'projectgroups' | 'grants' | 'memberships' | 'explorer';

const TABS: { id: Tab; label: string; icon: typeof Shield }[] = [
  { id: 'roles', label: 'Roles', icon: Shield },
  { id: 'usergroups', label: 'User Groups', icon: Users2 },
  { id: 'projectgroups', label: 'Project Groups', icon: FolderTree },
  { id: 'grants', label: 'Assignments', icon: KeyRound },
  { id: 'memberships', label: 'Members', icon: UserCheck },
  { id: 'explorer', label: 'Explorer', icon: Search },
];

const SCOPE_KINDS = ['global', 'org', 'program', 'releasetrain', 'project', 'projectgroup'] as const;
const SUBJECT_KINDS = ['usergroup', 'user', 'token'] as const;
const MEMBER_STATUS = ['active', 'invited', 'suspended'] as const;

export default function AdminAccessPage() {
  const [tab, setTab] = useState<Tab>('roles');
  const [orgs, setOrgs] = useState<any[]>([]);
  const [orgId, setOrgId] = useState<string>('');
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [tokens, setTokens] = useState<any[]>([]);
  const [vocab, setVocab] = useState<{ permissions: string[]; modules: string[] }>({ permissions: [], modules: [] });

  const [roles, setRoles] = useState<any[]>([]);
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [projectGroups, setProjectGroups] = useState<any[]>([]);
  const [grants, setGrants] = useState<any[]>([]);
  const [memberships, setMemberships] = useState<any[]>([]);

  const orgById = useMemo(() => Object.fromEntries(orgs.map((o) => [o._id, o])), [orgs]);
  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u._id, u])), [users]);
  const tokenById = useMemo(() => Object.fromEntries(tokens.map((t) => [t._id, t])), [tokens]);
  const roleById = useMemo(() => Object.fromEntries(roles.map((r) => [r._id, r])), [roles]);
  const groupById = useMemo(() => Object.fromEntries(userGroups.map((g) => [g._id, g])), [userGroups]);
  const projGroupById = useMemo(() => Object.fromEntries(projectGroups.map((g) => [g._id, g])), [projectGroups]);
  const projById = useMemo(() => Object.fromEntries(projects.map((p) => [p._id, p])), [projects]);

  useEffect(() => {
    organizationApi.list().then((o) => { setOrgs(o); if (o.length && !orgId) setOrgId(o[0]._id); }).catch(() => {});
    projectApi.list({ includeOperations: true }).then(setProjects).catch(() => {});
    userApi.list().then(setUsers).catch(() => {});
    apiTokenApi.list().then(setTokens).catch(() => {});
    rbacApi.vocabulary().then((v) => setVocab({ permissions: v.permissions, modules: v.modules })).catch(() => {});
  }, []); // eslint-disable-line

  const reload = useCallback(() => {
    if (!orgId) return;
    rbacApi.listRoles(orgId).then(setRoles).catch(() => {});
    rbacApi.listUserGroups(orgId).then(setUserGroups).catch(() => {});
    rbacApi.listProjectGroups(orgId).then(setProjectGroups).catch(() => {});
    rbacApi.listGrants({ orgId }).then(setGrants).catch(() => {});
    rbacApi.listMemberships({ orgId }).then(setMemberships).catch(() => {});
  }, [orgId]);
  useEffect(() => { reload(); }, [reload]);

  const orgProjects = useMemo(() => projects.filter((p) => !p.organizationId || p.organizationId === orgId), [projects, orgId]);

  const subjectLabel = (kind: string, id: string) =>
    kind === 'user' ? (userById[id]?.name || id)
      : kind === 'usergroup' ? (groupById[id]?.name || id)
        : kind === 'token' ? (tokenById[id]?.name || id) : id;
  const scopeLabel = (g: any) => g.scopeKind === 'global' ? 'everything (global)'
    : `${g.scopeKind}: ${orgById[g.scopeId]?.name || projById[g.scopeId]?.name || projGroupById[g.scopeId]?.name || g.scopeId}`;

  return (
    <AuthGuard roles={['super_admin']}>
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"><Shield size={20} /> Access Control</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Click any row to edit. Assignments are by group or role, not per-user.
              <span className="ml-1 text-amber-600 dark:text-amber-400">Enforcement is applied later — nothing here changes access yet.</span>
            </p>
          </div>
          <label className="text-sm flex items-center gap-2">
            <span className="text-gray-500 dark:text-gray-400">Organization</span>
            <select value={orgId} onChange={(e) => setOrgId(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md">
              {orgs.map((o) => <option key={o._id} value={o._id}>{o.parentOrgId ? `— ${o.name}` : o.name}</option>)}
            </select>
          </label>
        </div>

        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-4">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.id ? 'border-accent text-accent font-medium' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'roles' && <RolesTab orgId={orgId} roles={roles} vocab={vocab} reload={reload} />}
        {tab === 'usergroups' && <UserGroupsTab orgId={orgId} groups={userGroups} users={users} userById={userById} reload={reload} />}
        {tab === 'projectgroups' && <ProjectGroupsTab orgId={orgId} groups={projectGroups} projects={orgProjects} projById={projById} reload={reload} />}
        {tab === 'grants' && (
          <GrantsTab orgId={orgId} grants={grants} roles={roles} users={users} userGroups={userGroups} tokens={tokens}
            projects={orgProjects} projectGroups={projectGroups} orgs={orgs} roleById={roleById}
            subjectLabel={subjectLabel} scopeLabel={scopeLabel} reload={reload} />
        )}
        {tab === 'memberships' && <MembershipsTab orgId={orgId} memberships={memberships} users={users} userById={userById} reload={reload} />}
        {tab === 'explorer' && <AccessExplorer users={users} tokens={tokens} projects={orgProjects} />}
      </div>
    </AuthGuard>
  );
}

// ── Row + Modal primitives ────────────────────────────────────────────────────
function Row({ onClick, children }: any) {
  return (
    <button onClick={onClick} className="w-full text-left flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 hover:border-accent/50 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
      <div className="min-w-0">{children}</div>
      <ChevronRight size={14} className="text-gray-300 dark:text-gray-600 shrink-0" />
    </button>
  );
}
function AddBtn({ onClick, label }: any) {
  return <div className="flex justify-end mb-2"><button onClick={onClick} className="text-sm flex items-center gap-1 px-3 py-1.5 bg-accent text-white rounded-md"><Plus size={14} /> {label}</button></div>;
}
function Modal({ title, children, onClose, onSave, onDelete, readOnly }: any) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"><X size={16} /></button>
        </div>
        {children}
        <div className="flex items-center justify-between mt-4">
          <div>{onDelete && <button onClick={onDelete} className="text-sm flex items-center gap-1 px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50"><Trash2 size={14} /> Delete</button>}</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm px-3 py-1.5 rounded border border-gray-200 dark:border-gray-600">{readOnly ? 'Close' : 'Cancel'}</button>
            {!readOnly && <button onClick={onSave} className="text-sm flex items-center gap-1 px-3 py-1.5 bg-accent text-white rounded-md"><Save size={14} /> Save</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md mb-2';
const inputSm = 'px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md w-full';
function PermGroup({ title, perms, selected, toggle, label, readOnly }: any) {
  return (
    <div className="mt-3">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {perms.map((p: string) => (
          <button key={p} disabled={readOnly} onClick={() => !readOnly && toggle(p)}
            className={`text-[11px] px-2 py-1 rounded border ${selected.includes(p) ? 'bg-accent/10 border-accent text-accent' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'} ${readOnly ? 'cursor-default opacity-90' : ''}`}>
            {label(p)}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Roles ─────────────────────────────────────────────────────────────────────
function RolesTab({ orgId, roles, vocab, reload }: any) {
  const [editing, setEditing] = useState<any | null>(null);
  const ro = editing?.isSystem;
  const save = async () => {
    try {
      if (editing._id) await rbacApi.updateRole(editing._id, editing);
      else await rbacApi.createRole({ ...editing, orgId });
      toast.success('Role saved'); setEditing(null); reload();
    } catch (e: any) { toast.error(e.message || 'Save failed'); }
  };
  const del = async () => { if (!confirm(`Delete role "${editing.name}"?`)) return; await rbacApi.deleteRole(editing._id).then(() => { toast.success('Deleted'); setEditing(null); reload(); }).catch((e) => toast.error(e.message)); };
  const toggle = (p: string) => setEditing((s: any) => ({ ...s, permissions: s.permissions.includes(p) ? s.permissions.filter((x: string) => x !== p) : [...s.permissions, p] }));
  const dataPerms = vocab.permissions.filter((p: string) => !p.startsWith('module:') && p !== '*');
  const modulePerms = vocab.permissions.filter((p: string) => p.startsWith('module:'));
  return (
    <div>
      <AddBtn onClick={() => setEditing({ orgId, name: '', description: '', permissions: [] })} label="Custom role" />
      <div className="grid gap-2">
        {roles.map((r: any) => (
          <Row key={r._id} onClick={() => setEditing({ ...r })}>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.name} {r.isSystem && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500">system</span>}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{r.description} · {r.permissions.length} permissions</div>
          </Row>
        ))}
      </div>
      {editing && (
        <Modal title={ro ? `Role: ${editing.name} (system)` : editing._id ? 'Edit role' : 'New custom role'} readOnly={ro}
          onClose={() => setEditing(null)} onSave={save} onDelete={editing._id && !ro ? del : undefined}>
          {!ro && <input className={inputCls} placeholder="Role name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />}
          {!ro && <input className={inputCls} placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />}
          {ro && <p className="text-xs text-gray-500 mb-1">{editing.description}</p>}
          <PermGroup title="Modules (rail / features)" perms={modulePerms} selected={editing.permissions} toggle={toggle} readOnly={ro} label={(p: string) => p.replace('module:', '')} />
          <PermGroup title="Data permissions" perms={editing.permissions.includes('*') ? ['*', ...dataPerms] : dataPerms} selected={editing.permissions} toggle={toggle} readOnly={ro} label={(p: string) => p} />
        </Modal>
      )}
    </div>
  );
}

// ── User Groups ───────────────────────────────────────────────────────────────
function UserGroupsTab({ orgId, groups, users, userById, reload }: any) {
  const [editing, setEditing] = useState<any | null>(null);
  const save = async () => {
    try {
      if (editing._id) await rbacApi.updateUserGroup(editing._id, editing);
      else await rbacApi.createUserGroup({ ...editing, orgId });
      toast.success('Group saved'); setEditing(null); reload();
    } catch (e: any) { toast.error(e.message || 'Save failed'); }
  };
  const del = async () => { if (!confirm(`Delete group "${editing.name}"?`)) return; await rbacApi.deleteUserGroup(editing._id); setEditing(null); reload(); };
  const toggle = (uid: string) => setEditing((s: any) => ({ ...s, memberUserIds: s.memberUserIds.includes(uid) ? s.memberUserIds.filter((x: string) => x !== uid) : [...s.memberUserIds, uid] }));
  return (
    <div>
      <AddBtn onClick={() => setEditing({ orgId, name: '', description: '', memberUserIds: [] })} label="User group" />
      <div className="grid gap-2">
        {groups.map((g: any) => (
          <Row key={g._id} onClick={() => setEditing({ ...g })}>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{g.name}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{g.memberUserIds.length} members: {g.memberUserIds.map((id: string) => userById[id]?.name || id).slice(0, 5).join(', ')}</div>
          </Row>
        ))}
        {groups.length === 0 && <Empty>No user groups in this organization yet.</Empty>}
      </div>
      {editing && (
        <Modal title={editing._id ? 'Edit user group' : 'New user group'} onClose={() => setEditing(null)} onSave={save} onDelete={editing._id ? del : undefined}>
          <input className={inputCls} placeholder="Group name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          <input className={inputCls} placeholder="Description" value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
          <input className={inputCls} placeholder="Chat space for approval notices (e.g. training) — optional" value={editing.chatSpace || ''} onChange={(e) => setEditing({ ...editing, chatSpace: e.target.value })} />
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-2 mb-1">Members ({editing.memberUserIds.length})</div>
          <div className="max-h-60 overflow-y-auto grid gap-1">
            {users.map((u: any) => (
              <label key={u._id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.memberUserIds.includes(u._id)} onChange={() => toggle(u._id)} />{u.name} <span className="text-gray-400 text-xs">{u.email}</span></label>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Project Groups ────────────────────────────────────────────────────────────
function ProjectGroupsTab({ orgId, groups, projects, projById, reload }: any) {
  const [editing, setEditing] = useState<any | null>(null);
  const [filter, setFilter] = useState('');
  const save = async () => {
    try {
      if (editing._id) await rbacApi.updateProjectGroup(editing._id, editing);
      else await rbacApi.createProjectGroup({ ...editing, orgId });
      toast.success('Project group saved'); setEditing(null); reload();
    } catch (e: any) { toast.error(e.message || 'Save failed'); }
  };
  const del = async () => { if (!confirm(`Delete project group "${editing.name}"?`)) return; await rbacApi.deleteProjectGroup(editing._id); setEditing(null); reload(); };
  const toggle = (pid: string) => setEditing((s: any) => ({ ...s, projectIds: s.projectIds.includes(pid) ? s.projectIds.filter((x: string) => x !== pid) : [...s.projectIds, pid] }));
  const shown = editing ? projects.filter((p: any) => p.name.toLowerCase().includes(filter.toLowerCase())) : [];
  return (
    <div>
      <AddBtn onClick={() => { setFilter(''); setEditing({ orgId, name: '', description: '', projectIds: [] }); }} label="Project group" />
      <div className="grid gap-2">
        {groups.map((g: any) => (
          <Row key={g._id} onClick={() => { setFilter(''); setEditing({ ...g }); }}>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{g.name}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{g.projectIds.length} projects: {g.projectIds.map((id: string) => projById[id]?.name || id).slice(0, 5).join(', ')}{g.projectIds.length > 5 ? '…' : ''}</div>
          </Row>
        ))}
        {groups.length === 0 && <Empty>No project groups in this organization yet.</Empty>}
      </div>
      {editing && (
        <Modal title={editing._id ? 'Edit project group' : 'New project group'} onClose={() => setEditing(null)} onSave={save} onDelete={editing._id ? del : undefined}>
          <input className={inputCls} placeholder="Group name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          <input className={inputCls} placeholder="Description" value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
          <div className="flex items-center justify-between mt-2 mb-1">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Projects ({editing.projectIds.length})</div>
            <div className="flex gap-2 text-[11px]">
              <button className="text-accent" onClick={() => setEditing({ ...editing, projectIds: projects.map((p: any) => p._id) })}>Select all</button>
              <button className="text-gray-400" onClick={() => setEditing({ ...editing, projectIds: [] })}>Clear</button>
            </div>
          </div>
          <input className={inputSm + ' mb-1'} placeholder="filter projects…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          <div className="max-h-56 overflow-y-auto grid gap-1">
            {shown.map((p: any) => (
              <label key={p._id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.projectIds.includes(p._id)} onChange={() => toggle(p._id)} />{p.name} {p.displayId && <span className="text-gray-400 text-xs">{p.displayId}</span>}</label>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Grants ────────────────────────────────────────────────────────────────────
function GrantsTab({ orgId, grants, roles, users, userGroups, tokens, projects, projectGroups, orgs, roleById, subjectLabel, scopeLabel, reload }: any) {
  const [editing, setEditing] = useState<any | null>(null);
  const [kindFilter, setKindFilter] = useState<'all' | 'user' | 'usergroup' | 'token'>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const blank = { orgId, subjectKind: 'usergroup', subjectId: '', roleId: '', scopeKind: 'org', scopeId: orgId };

  const scopeOptions = (kind: string) =>
    kind === 'org' ? orgs.map((o: any) => ({ id: o._id, label: o.name }))
      : kind === 'project' ? projects.map((p: any) => ({ id: p._id, label: p.name }))
        : kind === 'projectgroup' ? projectGroups.map((g: any) => ({ id: g._id, label: g.name })) : [];
  const subjectOptions = (kind: string) =>
    kind === 'user' ? users.map((u: any) => ({ id: u._id, label: `${u.name} (${u.email})` }))
      : kind === 'token' ? tokens.map((t: any) => ({ id: t._id, label: t.name }))
        : userGroups.map((g: any) => ({ id: g._id, label: g.name }));

  const save = async () => {
    const f = editing;
    if (!f.subjectId || !f.roleId) { toast.error('Pick a subject and role'); return; }
    if (f.scopeKind !== 'global' && !f.scopeId) { toast.error('Pick a scope target'); return; }
    try {
      if (f._id) await rbacApi.deleteGrant(f._id); // grants are immutable — replace
      await rbacApi.createGrant({ orgId: f.orgId || orgId, subjectKind: f.subjectKind, subjectId: f.subjectId, roleId: f.roleId, scopeKind: f.scopeKind, scopeId: f.scopeKind === 'global' ? '' : f.scopeId });
      toast.success('Assignment saved'); setEditing(null); reload();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
  };
  const del = async () => { await rbacApi.deleteGrant(editing._id); setEditing(null); reload(); };
  const shown = grants.filter((g: any) => kindFilter === 'all' || g.subjectKind === kindFilter);

  // One card per subject. `summary` says what the scopes actually amount to, so
  // the collapsed row is informative on its own — "everything (global)" or
  // "3 groups · 2 projects" beats a bare count.
  const subjectGroups = useMemo(() => {
    const by = new Map<string, any>();
    for (const g of shown) {
      const key = `${g.subjectKind}:${g.subjectId}`;
      if (!by.has(key)) {
        by.set(key, { key, kind: g.subjectKind, subjectId: g.subjectId,
                      label: subjectLabel(g.subjectKind, g.subjectId), grants: [] });
      }
      by.get(key).grants.push(g);
    }
    return [...by.values()].map((sg) => {
      const counts: Record<string, number> = {};
      for (const g of sg.grants) counts[g.scopeKind] = (counts[g.scopeKind] || 0) + 1;
      const summary = counts.global
        ? 'everything (global)'
        : ['org', 'projectgroup', 'project', 'program', 'releasetrain']
            .filter((k) => counts[k])
            .map((k) => `${counts[k]} ${k === 'projectgroup' ? 'group' : k}${counts[k] > 1 ? 's' : ''}`)
            .join(' · ');
      return {
        ...sg,
        summary,
        roles: [...new Set(sg.grants.map((g: any) => roleById[g.roleId]?.name || g.roleId))].slice(0, 2),
      };
    }).sort((a, b) => b.grants.length - a.grants.length || a.label.localeCompare(b.label));
  }, [shown, roleById, subjectLabel]);

  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        An assignment reads as a sentence: <span className="font-medium text-gray-700 dark:text-gray-300">who</span> → <span className="font-medium text-gray-700 dark:text-gray-300">can do</span> (role) → <span className="font-medium text-gray-700 dark:text-gray-300">applies to</span> (scope).
      </p>
      <div className="flex items-center justify-between mb-2">
        <div className="flex gap-1">
          {(['all', 'user', 'usergroup', 'token'] as const).map((k) => (
            <button key={k} onClick={() => setKindFilter(k)} className={`text-[11px] px-2 py-1 rounded ${kindFilter === k ? 'bg-accent text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
              {k === 'all' ? 'All' : k === 'usergroup' ? 'Groups' : k === 'user' ? 'Users' : 'Tokens'} {k !== 'all' && <span className="opacity-70">{grants.filter((g: any) => g.subjectKind === k).length}</span>}
            </button>
          ))}
        </div>
        <button onClick={() => setEditing({ ...blank })} className="text-sm flex items-center gap-1 px-3 py-1.5 bg-accent text-white rounded-md"><Plus size={14} /> Assignment</button>
      </div>
      {/* Grouped by subject. A flat list rendered one row per project, so a
          single token with 62 projects produced 62 identical-looking rows and
          the page read as though it were full of duplicates. One card per
          subject, its scopes inside it. */}
      <div className="grid gap-2">
        {subjectGroups.map((sg: any) => {
          const open = expanded.has(sg.key);
          return (
            <div key={sg.key} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <button
                onClick={() => setExpanded((prev: Set<string>) => {
                  const next = new Set(prev);
                  next.has(sg.key) ? next.delete(sg.key) : next.add(sg.key);
                  return next;
                })}
                className="w-full flex items-center gap-2 px-3 py-2 text-left"
              >
                <ChevronRight
                  size={14}
                  className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
                />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {sg.label}
                </span>
                <span className="text-[11px] text-gray-400">({sg.kind})</span>
                <span className="ml-auto flex items-center gap-1.5 shrink-0">
                  {sg.roles.map((r: string) => (
                    <span key={r} className="text-[11px] px-1.5 py-0.5 rounded bg-accent/10 text-accent">{r}</span>
                  ))}
                  <span className="text-[11px] text-gray-400">
                    {sg.summary}
                  </span>
                </span>
              </button>
              {open && (
                <div className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                  {sg.grants.map((g: any) => (
                    <button
                      key={g._id}
                      onClick={() => setEditing({ ...g })}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {roleById[g.roleId]?.name || g.roleId}
                      </span>
                      <span className="text-gray-300">@</span>
                      <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{scopeLabel(g)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {subjectGroups.length === 0 && <Empty>No assignments{kindFilter !== 'all' ? ` for ${kindFilter}` : ''} in this organization.</Empty>}
      </div>
      {editing && (
        <Modal title={editing._id ? 'Edit assignment' : 'New assignment'} onClose={() => setEditing(null)} onSave={save} onDelete={editing._id ? del : undefined}>
          <Field label="Who — type">
            <select className={inputSm} value={editing.subjectKind} onChange={(e) => setEditing({ ...editing, subjectKind: e.target.value, subjectId: '' })}>
              {SUBJECT_KINDS.map((k) => <option key={k} value={k}>{k === 'usergroup' ? 'User group' : k === 'user' ? 'User' : 'API token'}</option>)}
            </select>
          </Field>
          <Field label="Who">
            <select className={inputSm} value={editing.subjectId} onChange={(e) => setEditing({ ...editing, subjectId: e.target.value })}>
              <option value="">— pick —</option>{subjectOptions(editing.subjectKind).map((o: any) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Can do (role)">
            <select className={inputSm} value={editing.roleId} onChange={(e) => setEditing({ ...editing, roleId: e.target.value })}>
              <option value="">— pick —</option>{roles.map((r: any) => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Applies to">
              <select className={inputSm} value={editing.scopeKind} onChange={(e) => setEditing({ ...editing, scopeKind: e.target.value, scopeId: e.target.value === 'org' ? orgId : '' })}>
                {SCOPE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </Field>
            <Field label="Which">
              <select className={inputSm} disabled={editing.scopeKind === 'global'} value={editing.scopeId} onChange={(e) => setEditing({ ...editing, scopeId: e.target.value })}>
                <option value="">{editing.scopeKind === 'global' ? '(all)' : '— pick —'}</option>
                {scopeOptions(editing.scopeKind).map((o: any) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Memberships ───────────────────────────────────────────────────────────────
function MembershipsTab({ orgId, memberships, users, userById, reload }: any) {
  const [editing, setEditing] = useState<any | null>(null);
  const [userId, setUserId] = useState('');
  const nonMembers = users.filter((u: any) => !memberships.some((m: any) => m.userId === u._id));
  const add = async () => { if (!userId) return; try { await rbacApi.createMembership({ userId, orgId }); setUserId(''); reload(); } catch (e: any) { toast.error(e.message); } };
  const save = async () => { await rbacApi.updateMembership(editing._id, { status: editing.status }).then(() => { toast.success('Saved'); setEditing(null); reload(); }).catch((e) => toast.error(e.message)); };
  const del = async () => { await rbacApi.deleteMembership(editing._id); setEditing(null); reload(); };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        <select className={inputSm + ' flex-1'} value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">— add a member —</option>{nonMembers.map((u: any) => <option key={u._id} value={u._id}>{u.name} ({u.email})</option>)}
        </select>
        <button onClick={add} className="text-sm flex items-center gap-1 px-3 py-2 bg-accent text-white rounded-md"><Plus size={14} /> Add</button>
      </div>
      <div className="grid gap-2">
        {memberships.map((m: any) => (
          <Row key={m._id} onClick={() => setEditing({ ...m })}>
            <div className="text-sm text-gray-900 dark:text-gray-100">{userById[m.userId]?.name || m.userId} <span className="text-gray-400">{userById[m.userId]?.email}</span></div>
            <div className="text-xs text-gray-500">status: {m.status}</div>
          </Row>
        ))}
        {memberships.length === 0 && <Empty>No members in this organization yet.</Empty>}
      </div>
      {editing && (
        <Modal title={`Member: ${userById[editing.userId]?.name || editing.userId}`} onClose={() => setEditing(null)} onSave={save} onDelete={del}>
          <Field label="Status">
            <select className={inputSm} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
              {MEMBER_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children }: any) { return <div className="mb-1"><div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">{label}</div>{children}</div>; }
function Empty({ children }: any) { return <div className="text-sm text-gray-400 py-6 text-center">{children}</div>; }
